import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { API } from '../../App';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { toast } from 'sonner';
import { ArrowLeft, Trophy } from 'lucide-react';

export default function AddAward({ user }) {
  const navigate = useNavigate();
  const location = useLocation();
  const editingAward = location.state?.award;
  const editingIndex = location.state?.index;
  
  const [formData, setFormData] = useState(editingAward || {
    event_name: '',
    competition_item: '',
    ranking: '',
    date: new Date().toISOString().split('T')[0]
  });
  const [loading, setLoading] = useState(false);
  const [config, setConfig] = useState({ event_names: [], competition_items: [] });
  const [useCustomEventName, setUseCustomEventName] = useState(false);
  const [useCustomCompetitionItem, setUseCustomCompetitionItem] = useState(false);

  useEffect(() => {
    loadConfig();
  }, []);

  const loadConfig = async () => {
    try {
      const res = await axios.get(`${API}/award-config?organization_id=${user.organization_id}`);
      setConfig(res.data);
      
      // If editing and value not in config, enable custom input
      if (editingAward) {
        if (!res.data.event_names.includes(editingAward.event_name)) {
          setUseCustomEventName(true);
        }
        if (!res.data.competition_items.includes(editingAward.competition_item)) {
          setUseCustomCompetitionItem(true);
        }
      }
    } catch (error) {
      console.error('載入配置錯誤:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.event_name || !formData.competition_item || !formData.ranking) {
      toast.error('請填寫所有必填欄位');
      return;
    }

    setLoading(true);
    try {
      // Fetch latest user data first to avoid data loss
      const userResponse = await axios.get(`${API}/auth/me`);
      const latestUser = userResponse.data;
      
      const currentAwards = latestUser.awards || [];
      let updatedAwards;
      
      if (editingIndex !== undefined) {
        // Edit mode: replace the award at editingIndex
        updatedAwards = [...currentAwards];
        updatedAwards[editingIndex] = formData;
      } else {
        // Add mode: append new award
        updatedAwards = [...currentAwards, formData];
      }
      
      await axios.put(`${API}/users/${user.user_id}`, {
        awards: updatedAwards
      });
      
      toast.success(editingIndex !== undefined ? '獲獎紀錄已更新' : '獲獎紀錄已新增');
      navigate('/student/profile');
    } catch (error) {
      console.error('操作獲獎紀錄錯誤:', error);
      toast.error('操作失敗：' + (error.response?.data?.detail || '未知錯誤'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 p-4 md:p-8">
      <div className="max-w-2xl mx-auto">
        {/* Header */}
        <div className="mb-6">
          <Button
            variant="ghost"
            onClick={() => navigate('/student/profile')}
            className="mb-4"
          >
            <ArrowLeft size={20} className="mr-2" />
            返回
          </Button>
          <div className="flex items-center gap-3">
            <Trophy className="text-yellow-500" size={32} />
            <h1 className="text-2xl md:text-3xl font-bold text-gray-800">
              {editingIndex !== undefined ? '編輯獲獎紀錄' : '新增獲獎紀錄'}
            </h1>
          </div>
        </div>

        {/* Form */}
        <div className="bg-white rounded-2xl shadow-lg p-6 md:p-8">
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* 賽事名稱 */}
            <div>
              <label className="text-sm font-medium text-gray-700 mb-2 block">
                賽事名稱 <span className="text-red-500">*</span>
              </label>
              
              {!useCustomEventName && config.event_names.length > 0 ? (
                <div className="space-y-2">
                  <Select 
                    value={formData.event_name} 
                    onValueChange={(value) => setFormData({...formData, event_name: value})}
                  >
                    <SelectTrigger className="text-base">
                      <SelectValue placeholder="請選擇賽事名稱" />
                    </SelectTrigger>
                    <SelectContent>
                      {config.event_names.map((name, index) => (
                        <SelectItem key={index} value={name}>{name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => setUseCustomEventName(true)}
                    className="text-blue-600 hover:text-blue-700"
                  >
                    使用自訂名稱
                  </Button>
                </div>
              ) : (
                <div className="space-y-2">
                  <Input
                    placeholder="例如：2024年全國青少年游泳錦標賽"
                    value={formData.event_name}
                    onChange={(e) => setFormData({...formData, event_name: e.target.value})}
                    required
                    className="text-base"
                  />
                  {config.event_names.length > 0 && (
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => {
                        setUseCustomEventName(false);
                        setFormData({...formData, event_name: ''});
                      }}
                      className="text-blue-600 hover:text-blue-700"
                    >
                      選擇預設名稱
                    </Button>
                  )}
                </div>
              )}
            </div>

            {/* 比賽項目 */}
            <div>
              <label className="text-sm font-medium text-gray-700 mb-2 block">
                比賽項目 <span className="text-red-500">*</span>
              </label>
              
              {!useCustomCompetitionItem && config.competition_items.length > 0 ? (
                <div className="space-y-2">
                  <Select 
                    value={formData.competition_item} 
                    onValueChange={(value) => setFormData({...formData, competition_item: value})}
                  >
                    <SelectTrigger className="text-base">
                      <SelectValue placeholder="請選擇比賽項目" />
                    </SelectTrigger>
                    <SelectContent>
                      {config.competition_items.map((item, index) => (
                        <SelectItem key={index} value={item}>{item}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => setUseCustomCompetitionItem(true)}
                    className="text-blue-600 hover:text-blue-700"
                  >
                    使用自訂項目
                  </Button>
                </div>
              ) : (
                <div className="space-y-2">
                  <Input
                    placeholder="例如：男子100米自由泳"
                    value={formData.competition_item}
                    onChange={(e) => setFormData({...formData, competition_item: e.target.value})}
                    required
                    className="text-base"
                  />
                  {config.competition_items.length > 0 && (
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => {
                        setUseCustomCompetitionItem(false);
                        setFormData({...formData, competition_item: ''});
                      }}
                      className="text-blue-600 hover:text-blue-700"
                    >
                      選擇預設項目
                    </Button>
                  )}
                </div>
              )}
            </div>

            <div>
              <label className="text-sm font-medium text-gray-700 mb-2 block">
                獲得名次 <span className="text-red-500">*</span>
              </label>
              <Select 
                value={formData.ranking} 
                onValueChange={(value) => setFormData({...formData, ranking: value})}
              >
                <SelectTrigger className="text-base">
                  <SelectValue placeholder="請選擇名次" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="冠軍">🥇 冠軍</SelectItem>
                  <SelectItem value="亞軍">🥈 亞軍</SelectItem>
                  <SelectItem value="季軍">🥉 季軍</SelectItem>
                  <SelectItem value="優異">⭐ 優異</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <label className="text-sm font-medium text-gray-700 mb-2 block">
                比賽日期
              </label>
              <Input
                type="date"
                value={formData.date}
                onChange={(e) => setFormData({...formData, date: e.target.value})}
                className="text-base"
              />
            </div>

            <div className="pt-4">
              <Button
                type="submit"
                className="w-full bg-yellow-500 hover:bg-yellow-600 text-base py-6"
                disabled={loading}
              >
                {loading ? '提交中...' : (editingIndex !== undefined ? '更新' : '提交')}
              </Button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
